import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

// Constantes de permisos disponibles en el sistema
export const PERMISSIONS = {
  // Documentos
  DOCUMENT_READ: 'document.read',
  DOCUMENT_WRITE: 'document.write',
  DOCUMENT_DELETE: 'document.delete',
  DOCUMENT_UPLOAD: 'document.upload',
  DOCUMENT_PROCESS: 'document.process',
  DOCUMENT_CREATE: 'document.create',
  
  // Pagos
  PAYMENT_READ: 'payment.read',
  PAYMENT_CREATE: 'payment.create',
  PAYMENT_UPDATE: 'payment.update',
  PAYMENT_VERIFY: 'payment.verify',
  
  // Dispersión
  DISPERSION_READ: 'dispersion.read',
  DISPERSION_CREATE: 'dispersion.create',
  DISPERSION_APPROVE: 'dispersion.approve',
  DISPERSION_EXECUTE: 'dispersion.execute',
  
  // Cuentas bancarias
  BANKACCOUNT_READ: 'bankaccount.read',
  BANKACCOUNT_WRITE: 'bankaccount.write',
  BANKACCOUNT_DELETE: 'bankaccount.delete',
  
  // Conceptos
  CONCEPT_READ: 'concept.read',
  CONCEPT_WRITE: 'concept.write',
  CONCEPT_DELETE: 'concept.delete',
  
  // Usuarios
  USER_READ: 'user.read',
  USER_WRITE: 'user.write',
  USER_DELETE: 'user.delete',
  
  // Roles
  ROLE_READ: 'role.read',
  ROLE_WRITE: 'role.write',
  ROLE_ASSIGN: 'role.assign',
  
  // Reportes
  REPORT_READ: 'report.read',
  REPORT_EXPORT: 'report.export',
  
  // Auditoría
  AUDIT_READ: 'audit.read',
  
  // Administración
  ADMIN_SYSTEM: 'admin.system',
  ADMIN_TENANT: 'admin.tenant',
  ADMIN_SETTINGS: 'admin.settings'
} as const;

// Array con todos los permisos disponibles
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private userPermissions: string[] = [];
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();

  constructor() {}

  /**
   * Inicializar permisos del usuario desde localStorage o API
   */
  initializePermissions(): void {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    if (userData && userData.assignedRoles) {
      this.loadPermissionsFromRoles(userData.assignedRoles);
    }
  }

  /**
   * Cargar permisos desde los roles asignados al usuario
   */
  private loadPermissionsFromRoles(assignedRoles: any[]): void {
    let allPermissions: string[] = [];
    
    // Por cada rol asignado, agregar sus permisos
    assignedRoles.forEach(role => {
      // Si el rol tiene permisos directamente (estructura futura)
      if (role.permissions && Array.isArray(role.permissions)) {
        allPermissions = [...allPermissions, ...role.permissions];
      } else {
        // Si no tiene permisos directos, usar permisos por defecto basados en roleId
        const defaultPermissions = this.getDefaultPermissionsByRoleId(role.roleId || role.roleName);
        allPermissions = [...allPermissions, ...defaultPermissions];
      }
    });

    // Eliminar duplicados
    this.userPermissions = [...new Set(allPermissions)];
    this.permissionsSubject.next(this.userPermissions);
    
    console.log('User permissions loaded:', this.userPermissions);
  }

  /**
   * Obtiene permisos por defecto basado en el roleId
   */
  private getDefaultPermissionsByRoleId(roleId: string): string[] {
    if (!roleId) return [];
    
    const roleIdLower = roleId.toLowerCase().trim();
    
    switch (roleIdLower) {
      case 'gestor':
        return [
          // Documentos - puede leer, escribir, subir y procesar
          PERMISSIONS.DOCUMENT_READ,
          PERMISSIONS.DOCUMENT_WRITE, 
          PERMISSIONS.DOCUMENT_UPLOAD,
          PERMISSIONS.DOCUMENT_PROCESS,
          // Pagos - puede leer y crear
          PERMISSIONS.PAYMENT_READ,
          PERMISSIONS.PAYMENT_CREATE,
          // Conceptos - solo lectura
          PERMISSIONS.CONCEPT_READ,
          // Cuentas bancarias - solo lectura
          PERMISSIONS.BANKACCOUNT_READ,
          // Reportes - solo lectura
          PERMISSIONS.REPORT_READ
        ];
        
      case 'supervisor':
        return [
          // Documentos - puede leer y escribir
          PERMISSIONS.DOCUMENT_READ,
          PERMISSIONS.DOCUMENT_WRITE,
          // Pagos - puede leer y verificar
          PERMISSIONS.PAYMENT_READ,
          PERMISSIONS.PAYMENT_VERIFY,
          // Dispersión - puede leer y aprobar
          PERMISSIONS.DISPERSION_READ,
          PERMISSIONS.DISPERSION_APPROVE,
          // Reportes y auditoría
          PERMISSIONS.REPORT_READ,
          PERMISSIONS.AUDIT_READ,
          // Usuarios - solo lectura para supervisión
          PERMISSIONS.USER_READ
        ];
        
      case 'controlador':
        return [
          // Cuentas bancarias - acceso completo
          PERMISSIONS.BANKACCOUNT_READ,
          PERMISSIONS.BANKACCOUNT_WRITE,
          PERMISSIONS.BANKACCOUNT_DELETE,
          // Conceptos - acceso completo
          PERMISSIONS.CONCEPT_READ,
          PERMISSIONS.CONCEPT_WRITE,
          PERMISSIONS.CONCEPT_DELETE,
          // Dispersión - puede leer, crear y ejecutar
          PERMISSIONS.DISPERSION_READ,
          PERMISSIONS.DISPERSION_CREATE,
          PERMISSIONS.DISPERSION_EXECUTE,
          // Reportes - puede leer y exportar
          PERMISSIONS.REPORT_READ,
          PERMISSIONS.REPORT_EXPORT,
          // Pagos - solo lectura para control
          PERMISSIONS.PAYMENT_READ
        ];
        
      case 'admin':
      case 'administrador':
        // Administrador tiene TODOS los permisos
        return ALL_PERMISSIONS;

      default:
        // Rol básico por defecto - solo permisos de lectura básicos
        return [
          PERMISSIONS.DOCUMENT_READ,
          PERMISSIONS.PAYMENT_READ,
          PERMISSIONS.CONCEPT_READ
        ];
    }
  }

  /**
   * Verificar si el usuario tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    return this.userPermissions.includes(permission);
  }

  /**
   * Verificar si el usuario tiene todos los permisos especificados
   */
  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * Verificar si el usuario tiene al menos uno de los permisos especificados
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Obtener todos los permisos del usuario
   */
  getAllPermissions(): string[] {
    return [...this.userPermissions];
  }

  /**
   * Limpiar permisos (útil al cerrar sesión)
   */
  clearPermissions(): void {
    this.userPermissions = [];
    this.permissionsSubject.next([]);
  }

  /**
   * Verificar permisos por categoría (ej: 'document', 'payment', etc.)
   */
  hasPermissionForCategory(category: string, action?: string): boolean {
    if (action) {
      return this.hasPermission(`${category}.${action}`);
    }
    
    // Si no se especifica acción, verificar si tiene algún permiso de esa categoría
    return this.userPermissions.some(permission => permission.startsWith(`${category}.`));
  }

  /**
   * Verificar si es administrador (tiene permisos de admin)
   */
  isAdmin(): boolean {
    return this.userPermissions.some(permission => permission.startsWith('admin.'));
  }

  /**
   * Actualizar permisos cuando se actualiza el usuario
   */
  updatePermissions(assignedRoles: any[]): void {
    this.loadPermissionsFromRoles(assignedRoles);
  }

  /**
   * Métodos específicos para verificar permisos por categoría
   */

  // Permisos de documentos
  canReadDocuments(): boolean { return this.hasPermission(PERMISSIONS.DOCUMENT_READ); }
  canWriteDocuments(): boolean { return this.hasPermission(PERMISSIONS.DOCUMENT_WRITE); }
  canDeleteDocuments(): boolean { return this.hasPermission(PERMISSIONS.DOCUMENT_DELETE); }
  canUploadDocuments(): boolean { return this.hasPermission(PERMISSIONS.DOCUMENT_UPLOAD); }
  canProcessDocuments(): boolean { return this.hasPermission(PERMISSIONS.DOCUMENT_PROCESS); }

  // Permisos de pagos
  canReadPayments(): boolean { return this.hasPermission(PERMISSIONS.PAYMENT_READ); }
  canCreatePayments(): boolean { return this.hasPermission(PERMISSIONS.PAYMENT_CREATE); }
  canUpdatePayments(): boolean { return this.hasPermission(PERMISSIONS.PAYMENT_UPDATE); }
  canVerifyPayments(): boolean { return this.hasPermission(PERMISSIONS.PAYMENT_VERIFY); }

  // Permisos de dispersión
  canReadDispersion(): boolean { return this.hasPermission(PERMISSIONS.DISPERSION_READ); }
  canCreateDispersion(): boolean { return this.hasPermission(PERMISSIONS.DISPERSION_CREATE); }
  canApproveDispersion(): boolean { return this.hasPermission(PERMISSIONS.DISPERSION_APPROVE); }
  canExecuteDispersion(): boolean { return this.hasPermission(PERMISSIONS.DISPERSION_EXECUTE); }

  // Permisos de cuentas bancarias
  canReadBankAccounts(): boolean { return this.hasPermission(PERMISSIONS.BANKACCOUNT_READ); }
  canWriteBankAccounts(): boolean { return this.hasPermission(PERMISSIONS.BANKACCOUNT_WRITE); }
  canDeleteBankAccounts(): boolean { return this.hasPermission(PERMISSIONS.BANKACCOUNT_DELETE); }

  // Permisos de conceptos
  canReadConcepts(): boolean { return this.hasPermission(PERMISSIONS.CONCEPT_READ); }
  canWriteConcepts(): boolean { return this.hasPermission(PERMISSIONS.CONCEPT_WRITE); }
  canDeleteConcepts(): boolean { return this.hasPermission(PERMISSIONS.CONCEPT_DELETE); }

  // Permisos de usuarios
  canReadUsers(): boolean { return this.hasPermission(PERMISSIONS.USER_READ); }
  canWriteUsers(): boolean { return this.hasPermission(PERMISSIONS.USER_WRITE); }
  canDeleteUsers(): boolean { return this.hasPermission(PERMISSIONS.USER_DELETE); }

  // Permisos de roles
  canReadRoles(): boolean { return this.hasPermission(PERMISSIONS.ROLE_READ); }
  canWriteRoles(): boolean { return this.hasPermission(PERMISSIONS.ROLE_WRITE); }
  canAssignRoles(): boolean { return this.hasPermission(PERMISSIONS.ROLE_ASSIGN); }

  // Permisos de reportes
  canReadReports(): boolean { return this.hasPermission(PERMISSIONS.REPORT_READ); }
  canExportReports(): boolean { return this.hasPermission(PERMISSIONS.REPORT_EXPORT); }

  // Permisos de auditoría
  canReadAudit(): boolean { return this.hasPermission(PERMISSIONS.AUDIT_READ); }

  // Permisos de administración
  canAccessSystemAdmin(): boolean { return this.hasPermission(PERMISSIONS.ADMIN_SYSTEM); }
  canAccessTenantAdmin(): boolean { return this.hasPermission(PERMISSIONS.ADMIN_TENANT); }
  canAccessSettings(): boolean { return this.hasPermission(PERMISSIONS.ADMIN_SETTINGS); }

  /**
   * Verificar si tiene permisos completos en una categoría
   */
  hasFullDocumentAccess(): boolean {
    return this.hasAllPermissions([
      PERMISSIONS.DOCUMENT_READ, PERMISSIONS.DOCUMENT_WRITE, 
      PERMISSIONS.DOCUMENT_DELETE, PERMISSIONS.DOCUMENT_UPLOAD, 
      PERMISSIONS.DOCUMENT_PROCESS
    ]);
  }

  hasFullPaymentAccess(): boolean {
    return this.hasAllPermissions([
      PERMISSIONS.PAYMENT_READ, PERMISSIONS.PAYMENT_CREATE,
      PERMISSIONS.PAYMENT_UPDATE, PERMISSIONS.PAYMENT_VERIFY
    ]);
  }

  hasFullUserAccess(): boolean {
    return this.hasAllPermissions([
      PERMISSIONS.USER_READ, PERMISSIONS.USER_WRITE, PERMISSIONS.USER_DELETE
    ]);
  }

  hasFullRoleAccess(): boolean {
    return this.hasAllPermissions([
      PERMISSIONS.ROLE_READ, PERMISSIONS.ROLE_WRITE, PERMISSIONS.ROLE_ASSIGN
    ]);
  }
}